import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FlightLogFormBuilderContext } from 'src/app/flight-logs/flight-log-form-helper';
import {
  departureTimeMustFollowArrivalTime,
  flightLogEntryValidators,
  hnvgoNonDecimalValidator,
  pilotLogEntryValidator,
} from 'src/app/flight-logs/flight-log-form-validators';
import {
  ApproachType,
  BillingSummaryDto,
  ComponentDto,
  CrewOnboardDto,
  DepartureType,
  EngineMaintenanceDetailDto,
  FlightAdvantage,
  FlightLogEntryDto,
  FuelEventDto,
  LogEntryDetailAttributesDto,
  LogEntryDetailDto,
  LogMode,
  MaintenanceSummaryDto,
  PilotLogEntryDto,
  WaypointDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { CrewMemberDto } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { field } from 'src/util/angular/ng-forms-validation';
import { getKeyValues } from 'src/util/object';
import { getOrElse } from 'src/util/values';
import { flightLogValidators } from './flight-log-form-validators';

export function createWaypointFormGroup(
  context: FlightLogFormBuilderContext,
): (waypoint: WaypointDto) => FormGroup {
  const fb = context.fb;
  return (waypoint) =>
    fb.group(
      {
        id: waypoint.id,
        description: [
          waypoint.description,
          field('Origin/destination description (from/to)', { required: true }),
        ],
        coordinate: fb.group({
          latitude: waypoint.coordinate.latitude,
          longitude: waypoint.coordinate.longitude,
        }),
        arrivalTime: [
          waypoint.arrivalTime,
          Validators.compose([
            field('Arrival Time', {
              // whether or not time is required depends on the leg
              required: false,
              minYear: 2016,
              maxDate: 'current',
            }),
            // NOTE: validation for waypoint departure/arrival times should be done
            // as part of validation for the flight log ENTRY, as there may not be a departure
            // or arrival time if the entry is the first or final entry of the log
          ]),
        ],
        departureTime: [
          waypoint.departureTime,
          Validators.compose([
            field('Departure Time', {
              // whether or not time is required depends on the leg
              required: false,
              minYear: 2016,
              maxDate: 'current',
            }),
            // NOTE: validation for waypoint departure/arrival times should be done
            // as part of validation for the flight log ENTRY, as there may not be a departure
            // or arrival time if the entry is the first or final entry of the log
          ]),
        ],
        airportId: waypoint.airportId,
      },
      {
        validator: departureTimeMustFollowArrivalTime,
      },
    );
}

export function createEngineMaintenanceDetailFormGroup(
  context: FlightLogFormBuilderContext,
  engine: EngineMaintenanceDetailDto,
) {
  const fb = context.fb;
  const cycles = fb.group({});
  const cycleControls = getKeyValues(engine.cycles).map((kv) => {
    return {
      name: kv.key,
      control: new FormControl(
        getOrElse(kv.value, 0),
        field(`${kv.key} cycles`, { min: 0, max: 99999999 }),
      ),
    };
  });
  cycleControls.forEach(({ name, control }) => {
    cycles.addControl(name, control);
  });

  return fb.group({
    time: engine.time,
    starts: [engine.starts, field('Engine Starts', { min: 0 })],
    cycles,
  });
}

export function createMaintenanceComponent(
  context: FlightLogFormBuilderContext,
  component: ComponentDto,
) {
  return context.fb.group({
    flightTime: component.flightTime,
    nightTime: component.nightTime,
    cycles: component.cycles,
  });
}

export function createMaintenanceSummaryFormGroup(
  context: FlightLogFormBuilderContext,
  ms: MaintenanceSummaryDto,
): FormGroup {
  return context.fb.group({
    startingHobbsReading: [
      ms.startingHobbsReading,
      field('Starting Hobbs', { min: 0, max: 99999999 }),
    ],
    endingHobbsReading: [ms.endingHobbsReading, field('Ending Hobbs', { min: 0, max: 99999999 })],
    totalFlightTime: [
      getOrElse(ms.totalFlightTime, 0),
      field('Flight Time', {
        min: 0,
        required: true,
      }),
    ],
    // TODO: Need to figure out how to disable fields without effecting form value
    totalLandings: context.fb.control(
      { value: getOrElse(ms.totalLandings, 0), disabled: false },
      field('Total Landings', { min: 0 }),
    ),
    runOnLandings: context.fb.control(
      { value: getOrElse(ms.runOnLandings, 0), disabled: false },
      field('Total Run-On Landings', { min: 0 }),
    ),
    airframeTime: [ms.airframeTime, field('Aircraft Time', { min: 0 })],
    engine1: createEngineMaintenanceDetailFormGroup(context, ms.engine1),
    engine2: createEngineMaintenanceDetailFormGroup(context, ms.engine2),
    hook: createMaintenanceComponent(context, ms.hook),
    hoist: createMaintenanceComponent(context, ms.hoist),
  });
}

export function createBillingSummaryFormGroup(
  context: FlightLogFormBuilderContext,
  billingSummary: BillingSummaryDto,
) {
  const fb = context.fb;
  return fb.group({
    comment: [billingSummary.comment, field('Comment')],
    loadedLegCount: [billingSummary.loadedLegCount, field('Loaded leg count')],
    totalLegCount: [billingSummary.totalLegCount, field('Total leg count')],
    loadedMilesActual: [billingSummary.loadedMilesActual, field('Actual loaded miles')],
    loadedMilesCalculated: [billingSummary.loadedMilesCalculated, field('Calculated miles')],
  });
}

export function createFlightLogFormGroup(context: FlightLogFormBuilderContext) {
  // If we don't have a log entry, create one
  const flightLog = context.flightLog;

  const isSimple = flightLog.mode === LogMode.Simple;
  const form = context.fb.group(
    {
      metadata: flightLog.metadata,
      id: flightLog.id,
      updatedBy: flightLog.updatedBy,
      comment: flightLog.comment,
      status: flightLog.status,
      fromLocation: flightLog.fromLocation,
      toLocation: flightLog.toLocation,
      matowInKg: flightLog.matowInKg,
      maxAftCGInInches: flightLog.maxAftCGInInches,
      maxForeCGInInches: flightLog.maxForeCGInInches,
      flightType: [
        flightLog.flightType,
        field('Flight Type', {
          required: true,
        }),
      ],
      mode: flightLog.mode,
      maintenanceSummary:
        flightLog.maintenanceSummary != null
          ? createMaintenanceSummaryFormGroup(context, flightLog.maintenanceSummary)
          : null,
      flightCrew: context.fb.array(flightLog.flightCrew.map(createCrewMemberFormGroup(context))),
      flightSummaryId: [
        flightLog.flightSummaryId,
        field('Flight #', {
          // per pbi2013: Remove Flight Number Requirement Validation
          required: false,
        }),
      ],
      flightTagNumber: [flightLog.flightTagNumber, field('Flight Tag #')],
      companyId: [
        flightLog.companyId != null ? flightLog.companyId : context.user.companycode,
        field('Company', { required: !isSimple }),
      ],
      baseId: [flightLog.baseId, field('Base', { required: !isSimple })],
      aircraftId: [flightLog.aircraftId, field('Aircraft', { required: !isSimple })],
      advantages: createFlightAdvantagesFormArray(context, flightLog.advantages),
      waypoints: context.fb.array(flightLog.waypoints.map(createWaypointFormGroup(context))),
      entries: context.fb.array(flightLog.entries.map(createLogEntryFormGroup(context))),
    },
    {
      validator: flightLogValidators(context),
    },
  );
  return form;
}

export function createFlightAdvantagesFormArray(
  context: FlightLogFormBuilderContext,
  advantages: FlightAdvantage[],
): FormArray {
  return context.fb.array(advantages.map((_) => [_]));
}

export function createCrewMemberFormGroup(
  context: FlightLogFormBuilderContext,
): (crewMember: CrewMemberDto) => FormGroup {
  return (crewMember) =>
    context.fb.group({
      id: crewMember.id,
      employeeId: crewMember.employeeId,
      firstName: crewMember.firstName,
      lastName: crewMember.lastName,
      role: crewMember.role,
    });
}

export function createLogEntryDetailAttributesFormGroup(
  context: FlightLogFormBuilderContext,
  attributes: LogEntryDetailAttributesDto,
): FormGroup {
  return context.fb.group({
    hasExtraMileage: attributes.hasExtraMileage,
    nvg: attributes.nvg,
    refueled: attributes.refueled,
  });
}

export function createLogEntryDetailFormGroup(
  context: FlightLogFormBuilderContext,
  entryDetail: LogEntryDetailDto,
): FormGroup {
  const waypointsRequired = context.flightLog.mode === LogMode.EntryPerLeg;
  return context.fb.group({
    attributes: createLogEntryDetailAttributesFormGroup(context, entryDetail.attributes),
    cabinTemperature: [
      entryDetail.cabinTemperature,
      field('Cabin temperature', {
        integer: true,
      }),
    ],
    fromWaypointId: [
      entryDetail.fromWaypointId,
      field('Waypoint (From)', { required: waypointsRequired }),
    ],
    toWaypointId: [
      entryDetail.toWaypointId,
      field('Waypoint (To)', { required: waypointsRequired }),
    ],
    maxAltitude: [
      entryDetail.maxAltitude,
      field('Max cabin altitude', {
        integer: true,
      }),
    ],
    isPatientOnboard: entryDetail.isPatientOnboard,
    passengerCount: [entryDetail.passengerCount, field('Passengers', { max: 100 })],
    totalLoadedWeightInKg: [
      entryDetail.totalLoadedWeightInKg,
      field('TLW(kg)', { max: 999999999999 }),
    ],
    actIndex: [entryDetail.actIndex, field('Act Index', { max: 99999999 })],
    billingSummary: createBillingSummaryFormGroup(context, entryDetail.billingSummary),
    fuelEvents: context.fb.array(entryDetail.fuelEvents.map(createFuelEventFormGroup(context))),
    crewOnboard: context.fb.array(entryDetail.crewOnboard.map(createCrewOnboardFormGroup(context))),
  });
}

export function createFuelEventFormGroup(
  context: FlightLogFormBuilderContext,
): (fuelEvent: FuelEventDto) => FormGroup {
  return (fuelEvent) =>
    context.fb.group({
      fboId: fuelEvent.fboId,
      fuelSourceType: [fuelEvent.fuelSourceType, field('Fuel Source Type', { required: true })],
      fuelTankId: fuelEvent.fuelTankId,
      paymentType: fuelEvent.paymentType,
      totalGallons: [fuelEvent.totalGallons, field('Total gallons', { min: 0 })],
    });
}

export function createCrewOnboardFormGroup(
  context: FlightLogFormBuilderContext,
): (crewMember: CrewOnboardDto) => FormGroup {
  return (crewMember) =>
    context.fb.group({
      flightCrewId: crewMember.flightCrewId,
      isNvg: crewMember.isNvg,
      hnvgo: [crewMember.hnvgo, hnvgoNonDecimalValidator()],
    });
}

export function createApproachTypeFormControl(
  context: FlightLogFormBuilderContext,
): (approachType: ApproachType) => FormControl {
  return (approachType) => context.fb.control(approachType);
}

export function createDepartureTypeFormControl(
  context: FlightLogFormBuilderContext,
): (departureType: DepartureType) => FormControl {
  return (departureType) => context.fb.control(departureType);
}

export function createPilotLogEntryFormGroup(
  context: FlightLogFormBuilderContext,
): (pilotLogEntry: PilotLogEntryDto) => FormGroup {
  return (pilotLogEntry) => {
    return context.fb.group(
      {
        flightCrewId: pilotLogEntry.flightCrewId,
        approaches: context.fb.array(
          pilotLogEntry.approaches.map(createApproachTypeFormControl(context)),
        ),
        departures: context.fb.array(
          pilotLogEntry.departures.map(createDepartureTypeFormControl(context)),
        ),
        verifiedAt: pilotLogEntry.verifiedAt,
        verifiedById: pilotLogEntry.verifiedById,
        hnvgo: [pilotLogEntry.hnvgo, field('HNVGO', { min: 0, integer: true, max: 99999999 })],
        blockStartTime: [
          pilotLogEntry.blockStartTime,
          field('Block Start Time', { minYear: 2016, maxYear: 'current' }),
        ],
        blockEndTime: [
          pilotLogEntry.blockEndTime,
          field('Block End Time', { minYear: 2016, maxYear: 'current' }),
        ],
        flightHours: context.fb.group({
          day: [pilotLogEntry.flightHours.day, field('Day Hours', { min: 0, max: 99999999 })],
          ifrImc: [pilotLogEntry.flightHours.ifrImc, field('IFR IMC', { min: 0, max: 99999999 })],
          ifrSim: [pilotLogEntry.flightHours.ifrSim, field('IFR SIM', { min: 0, max: 99999999 })],
          blockTime: [pilotLogEntry.flightHours.blockTime],
          nightAided: [
            pilotLogEntry.flightHours.nightAided,
            field('Night Aided Hours', { min: 0, max: 99999999 }),
          ],
          nightUnaided: [
            pilotLogEntry.flightHours.nightUnaided,
            field('Night Unaided Hours', { min: 0, max: 99999999 }),
          ],
          part135: [
            pilotLogEntry.flightHours.part135,
            field(
              'Part 135 Hours',
              context.features.part135part91 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part91: [
            pilotLogEntry.flightHours.part91,
            field(
              'Part 91 Hours',
              context.features.part135part91 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part133: [
            pilotLogEntry.flightHours.part133,
            field(
              'Part 133 Hours',
              context.features.part133 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part133HoistDayHours: [
            pilotLogEntry.flightHours.part133HoistDayHours,
            field(
              'Part 133 Hoist Hours',
              context.features.part133 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part133HookDayHours: [
            pilotLogEntry.flightHours.part133HookDayHours,
            field(
              'Part 133 Hook Hours',
              context.features.part133 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part133HoistNightHours: [
            pilotLogEntry.flightHours.part133HoistNightHours,
            field(
              'Part 133 Hoist Night Hours',
              context.features.part133 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
          part133HookNightHours: [
            pilotLogEntry.flightHours.part133HookNightHours,
            field(
              'Part 133 Hook Night Hours',
              context.features.part133 ? { min: 0, max: 99999999 } : undefined,
            ),
          ],
        }),
        landings: context.fb.group({
          day: [
            pilotLogEntry.landings.day,
            field('Day Landings', { min: 0, integer: true, max: 100 }),
          ],
          night: [
            pilotLogEntry.landings.night,
            field('Night Landings', { min: 0, integer: true, max: 100 }),
          ],
          runOn: [
            pilotLogEntry.landings.runOn,
            field('Run On Landings', { min: 0, integer: true, max: 100 }),
          ],
        }),
      },
      { validator: pilotLogEntryValidator(context) },
    );
  };
}

export function createLogEntryFormGroup(
  context: FlightLogFormBuilderContext,
): (entry: FlightLogEntryDto) => FormGroup {
  return (entry) => {
    return context.fb.group(
      {
        flightTime: [
          entry.flightTime,
          field(
            'Flight Time',
            context.flightLog.mode === LogMode.EntryPerFlight
              ? {
                  required: true,
                  minYear: 2016,
                  maxYear: 'current',
                }
              : {},
          ),
        ],
        entryDetail: createLogEntryDetailFormGroup(context, entry.entryDetail),
        pilotLogEntries: context.fb.array(
          entry.pilotLogEntries.map(createPilotLogEntryFormGroup(context)),
        ),
      },
      { validator: flightLogEntryValidators(context) },
    );
  };
}
